import React, { useState, useEffect, useRef } from 'react';
import './ControlInsumos.scss'
// PrimeReact imports
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
// Custom imports
import InputCalendario from "../../../components/atoms/InputCalendario/InputCalendario";
import { UpdateValue } from "../../../utilidades/Validaciones";
import { getSupplyRequests, updateProducts, updateSupplyRequests } from "./integracion";
import {InputText} from "primereact/inputtext";
import {OverlayPanel} from "primereact/overlaypanel";

const ControlInsumos = () => {
    // =========== STATE DEFINITIONS ===========
    const [dateFilters, setDateFilters] = useState({
        start: { value: new Date(new Date().setDate(new Date().getDate() - 30)) },
        end: { value: new Date() }
    });
    const [requests, setRequests] = useState([]);
    const [productDialog, setProductDialog] = useState(false);
    const [product, setProduct] = useState([]);
    const [requestUpdated, setRequestUpdated] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [drivers, setDrivers] = useState([]);
    const [statuses] = useState([
        { id: 1, name: 'NUEVO' },
        { id: 2, name: 'POR ENTREGAR' },
        { id: 3, name: 'INCOMPLETO' },
        { id: 4, name: 'ENTREGADO' },
        { id: 5, name: 'CANCELADO' }
    ]);
    const [filters, setFilters] = useState({
        CHOFER: { value: null, matchMode: FilterMatchMode.IN },
        ID_UNIDAD: { value: null, matchMode: FilterMatchMode.EQUALS },
        STATUS: { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    // =========== REFS ===========
    const toast = useRef(null);
    const dt = useRef(null);
    const op = useRef(null);

    // =========== EFFECTS ===========
    useEffect(() => {
        getSupplyRequests(setRequests, dateFilters, setDrivers);
    }, [dateFilters]);

    // =========== HANDLERS ===========
    const hideDialog = () => {
        setSubmitted(false);
        setProductDialog(false);
    };

    const saveProduct = () => {
        updateProducts(product).then(r => {
            setSubmitted(true);
            setProductDialog(false);
            toast.current.show({severity:'success', summary: 'Completado', detail:r.data, life: 3000});
            getSupplyRequests(setRequests, dateFilters, setDrivers);
        })
    };

    const editProduct = (product) => {
        if(product){
            setProduct([ ...product ]);
            setProductDialog(true);
        }
    };

    const handleNotesChange = (id, value) => {
        setProduct(product.map(product =>
            product.ID_DETALLE === id ? { ...product, OBSERVACIONES: value } : product
        ));
    };

    const handleEntregadoChange = (id, value) => {
        setProduct(product.map(product =>
            product.ID_DETALLE === id ? { ...product, ENTREGADO: value } : product
        ));
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const updateRequest = () => {
        updateSupplyRequests(requestUpdated).then(r => {
            setRequestUpdated([]);
            toast.current.show({severity:'success', summary: 'Completado', detail:r.data, life: 3000});
            getSupplyRequests(setRequests, dateFilters, setDrivers);
        }).catch(e => {
            toast.current.show({severity:'error', summary: 'Error', detail:'Error al guardar. Intentalo mas tarde', life: 3000});
        })
    }

    const onRowEditComplete = (e) => {
        const { newData } = e;
        setRequests(requests.map(product =>
            product.ID_SOLICITUD === newData.ID_SOLICITUD ? newData : product
        ));

        setRequestUpdated(prevState => {
            const alreadyExists = prevState.some(
                item => item.ID_SOLICITUD === newData.ID_SOLICITUD
            );

            if (alreadyExists) {
                return prevState.map(item =>
                    item.ID_SOLICITUD === newData.ID_SOLICITUD ? newData : item
                );
            } else {
                return [...prevState, newData];
            }
        });
    };

    // =========== TEMPLATES ===========
    const representativeBodyTemplate = (rowData) => {
        const representative = rowData.CHOFER;
        return (
            <div className="flex align-items-center gap-2">
                <span>{representative}</span>
            </div>
        );
    };

    const representativesItemTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <span>{option}</span>
            </div>
        );
    };

    const representativeRowFilterTemplate = (options) => {
        return (
            <MultiSelect
                value={options.value || []} // Asegura que no sea undefined
                options={drivers || []} // Evita que drivers sea undefined
                itemTemplate={representativesItemTemplate}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Ninguno"
                className="p-column-filter"
                maxSelectedLabels={1}
                filter
                style={{ minWidth: '10rem' }}
            />
        );
    };

    const statusEditor = (options) => {
        return (
            <Dropdown
                value={statuses.find((status) => status.name === options.value)}
                options={statuses}
                onChange={(e) => {
                    const selectedStatus = e.value;
                    options.editorCallback(selectedStatus.name);
                    options.rowData.ID_STATUS = selectedStatus.id;
                }}
                placeholder="Select a Status"
                optionLabel="name"
                itemTemplate={(option) => {
                    return <Tag value={option.name} severity={getSeverity(option.name)}></Tag>;
                }}
            />
        );
    };

    const statusItemTemplate = (option) => {
        return <Tag value={option} severity={getSeverity(option)} />;
    };

    const textEditor = (options) => {
        return <InputText type="text" value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />;
    };

    const statusRowFilterTemplate = (options) => {
        return (
            <Dropdown value={options.value}
                      options={statuses.map(item => item.name)}
                      onChange={(e) => options.filterApplyCallback(e.value)}
                      itemTemplate={statusItemTemplate}
                      placeholder="Selecciona uno"
                      className="p-column-filter"
                      showClear style={{maxWidth: '10rem'}}/>
        );
    };

    const statusBodyTemplate = (rowData) => {
        return <Tag value={rowData.STATUS} severity={getSeverity(rowData.STATUS)}></Tag>;
    };

    const actionBodyTemplate = (rowData) => {
        const details = JSON.parse(rowData.DETALLES);
        return (
            <React.Fragment>
                <Button icon="pi pi-eye" rounded outlined className="mr-2" onClick={() => editProduct(details)} />
            </React.Fragment>
        );
    };

    const notesTemplate = (rowData) => {
        return (
            <InputTextarea
                value={rowData.OBSERVACIONES}
                onChange={(e) => handleNotesChange(rowData.ID_DETALLE, e.target.value)}
                rows={2}
                autoResize
                placeholder="Agregar observación"
                className="w-full"
            />
        );
    };

     const notesPrincipalTemplate = (rowData) => {
        return (
            <InputTextarea
                value={rowData.OBSERVACIONES}
                onChange={(e) => handleNotesChange(rowData.ID_SOLICITUD, e.target.value)}
                rows={2}
                autoResize
                placeholder="Agregar observación"
                className="w-full"
            />
        );
    };

    const entregadoTemplate = (rowData) => (
        <InputNumber
            value={rowData.ENTREGADO}
            onValueChange={(e) => handleEntregadoChange(rowData.ID_DETALLE, e.target.value)}
            mode="decimal"
            min={0}
        />
    );

    const rightToolbarTemplate = () => {
        return <Button label="Descargar" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />;
    };

    const leftToolbarTemplate = () => {
        return (
            <div className='fila'>
                <div className="columna">
                    <InputCalendario value={dateFilters.start.value} id={"start"}
                                     onChange={(e) => {
                                         UpdateValue(e, "start", dateFilters, setDateFilters)
                                     }} titulo={"Fecha de Inicio"}></InputCalendario>
                </div>
                <div className="columna">
                    <InputCalendario value={dateFilters.end.value} id={"end"}
                                     onChange={(e) => {
                                         UpdateValue(e, "end", dateFilters, setDateFilters)
                                     }} titulo={"Fecha de Fin"}></InputCalendario>
                </div>
            </div>
        )
    }

    const imageTemplate = (rowData) => {
        return (
            <React.Fragment>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: 'center'}}>
                    <span>{rowData.INSUMO}</span>
                    {/*rowData.INSUMO === 'AdBlue' && (
                        <Button icon="pi pi-image" rounded text severity="info" onClick={(e) => op.current.toggle(e)}/>
                    )*/}
                </div>
                <OverlayPanel ref={op}>
                    <img src={'https://primefaces.org/cdn/primereact/images/product/bamboo-watch.jpg'} alt="Watch"></img>
                </OverlayPanel>
            </React.Fragment>
        )
    }

    // =========== UTILITY FUNCTIONS ===========
    const getSeverity = (status) => {
        switch (status) {
            case 'ENTREGADO': return 'success';
            case 'POR ENTREGAR': return 'warning';
            case 'CANCELADO': return 'danger';
            case 'INCOMPLETO': return 'secondary';
            case 'NUEVO': return 'info';
            default: return null;
        }
    };

    // =========== COMPONENT TEMPLATES ===========
    const header = (
        <div className="header-table">
            <h4 className="m-0">Solicitudes de insumos</h4>
            <Button label="Guardar" onClick={() => updateRequest()}/>
        </div>
    );

    const productDialogFooter = (
        <React.Fragment>
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={hideDialog} />
            <Button label="Aceptar" icon="pi pi-check" onClick={saveProduct} />
        </React.Fragment>
    );

    // =========== RENDER ===========
    return (
        <div>
            <Toast ref={toast}/>
            <div className="card">
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                <DataTable ref={dt}
                           value={requests}
                           dataKey="ID_SOLICITUD"
                           paginator rows={25}
                           rowsPerPageOptions={[25, 50, 100]}
                           filters={filters}
                           filterDisplay="row"
                           scrollable
                           scrollHeight="calc(100vh - 295px)"
                           emptyMessage="No existen solicitudes"
                           paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                           currentPageReportTemplate="Mostrando {first} - {last} de {totalRecords} solicitudes"
                           header={header}
                           size="small"
                           className="scaling-table insumos-table"
                           editMode="row"
                           onRowEditComplete={onRowEditComplete}
                           showGridlines
                >
                    <Column field="ID_SOLICITUD" header="ID" className="scaling-column"/>
                    <Column field="FECHA" header="Fecha" className="scaling-column" style={{minWidth: '8rem'}}/>
                    <Column field="FECHA_ENTREGA" header="Fecha entrega" className="scaling-column"/>
                    <Column field="ID_UNIDAD" filter filterPlaceholder="ID UNIDAD" style={{maxWidth: '12rem'}}
                            showFilterMenu={false} header="Unidad" className="scaling-column"/>
                    <Column field="CHOFER" filter filterPlaceholder="CHOFER" showFilterMenu={false} header="Chofer"
                            className="scaling-column" style={{maxWidth: '12rem'}} body={representativeBodyTemplate}
                            filterElement={representativeRowFilterTemplate}/>
                    <Column field="STATUS" header="Estatus" showFilterMenu={false} filterMenuStyle={{width: '8rem'}}
                            body={statusBodyTemplate} filter filterElement={statusRowFilterTemplate}
                            className="scaling-column" style={{maxWidth: '12rem'}}
                            editor={(options) => statusEditor(options)}/>
                    <Column field="OBSERVACIONES" header="Observaciones"  className="scaling-column"
                            editor={(options) => textEditor(options)} />
                    <Column body={actionBodyTemplate} exportable={false} className="scaling-column"/>
                    <Column rowEditor={true} headerStyle={{width: '10%', minWidth: '8rem'}}
                            bodyStyle={{textAlign: 'center'}}/>
                </DataTable>
            </div>

            <Dialog visible={productDialog} style={{width: '50rem'}} breakpoints={{'960px': '75vw', '641px': '90vw'}}
                    header="Productos solicitados" modal className="p-fluid" footer={productDialogFooter}
                    onHide={hideDialog}>
                <div className="card">
                    {product && (
                        <DataTable value={product} className="p-datatable-sm" dataKey="ID_DETALLE"
                                   tableStyle={{minWidth: '30rem'}}>
                            <Column field="INSUMO" header="Producto" body={imageTemplate}/>
                            <Column field="CANTIDAD" header="Cantidad" style={{textAlign: 'center'}}/>
                            <Column field="ENTREGADO" header="Entregado" body={entregadoTemplate}
                                    style={{textAlign: 'center'}}/>
                            <Column field="OBSERVACIONES" header="Observaciones" body={notesTemplate}/>
                        </DataTable>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

export default ControlInsumos;